import { useState, useEffect } from "react";
import { format, parseISO } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Edit2, Trash2, Plus } from "lucide-react";

const EntryModal = ({ 
  open, 
  onOpenChange, 
  selectedDate, 
  editingEntry, 
  categories, 
  entries,
  onSave,
  onDelete 
}) => {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("health");
  const [isAdding, setIsAdding] = useState(false);

  // Reset form when modal opens/closes or editing entry changes
  useEffect(() => {
    if (editingEntry) {
      setText(editingEntry.text);
      setCategory(editingEntry.category);
      setIsAdding(true);
    } else {
      setText("");
      setCategory("health");
      setIsAdding(entries?.length === 0);
    }
  }, [editingEntry, open, entries]);

  const handleSave = () => {
    if (!text.trim()) return;
    
    onSave({
      text: text.trim(),
      category
    });
    
    setText("");
    setCategory("health");
    setIsAdding(false);
  };

  const formattedDate = selectedDate 
    ? format(parseISO(selectedDate), "EEEE, MMMM d, yyyy")
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="entry-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {editingEntry ? "Edit Proud Moment" : "Add Proud Moment"}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{formattedDate}</p>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Existing entries for this date */}
          {!editingEntry && entries && entries.length > 0 && !isAdding && (
            <ScrollArea className="max-h-[200px]">
              <div className="space-y-2">
                {entries.map(entry => {
                  const cat = categories[entry.category];
                  return (
                    <div 
                      key={entry.entry_id}
                      className="p-3 bg-secondary/50 rounded-lg group flex items-start gap-3"
                    >
                      <div 
                        className="category-dot mt-1.5 flex-shrink-0"
                        style={{ backgroundColor: cat?.color || '#888' }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">{entry.text}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {cat?.name || entry.category}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            setText(entry.text);
                            setCategory(entry.category);
                            setIsAdding(true);
                          }}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => onDelete(entry.entry_id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}

          {/* Add another button */}
          {!editingEntry && entries && entries.length > 0 && !isAdding && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setIsAdding(true)}
              data-testid="add-another-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Proud Moment
            </Button>
          )}

          {/* Entry form */}
          {(isAdding || entries?.length === 0 || editingEntry) && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">What did you accomplish?</label>
                <Textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="I'm proud that I..."
                  className="min-h-[100px] resize-none"
                  data-testid="entry-text"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categories).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: cat.color }}
                          />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-2">
                {(entries?.length > 0 && !editingEntry) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsAdding(false);
                      setText("");
                      setCategory("health");
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={handleSave}
                  disabled={!text.trim()}
                  className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid="save-entry-btn"
                >
                  {editingEntry ? "Update Proud Moment" : "Save Proud Moment"}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EntryModal;
